import { useEffect, useState } from 'react';
import { Hash, AlertTriangle, Info } from 'lucide-react';
import { RepairActionCard } from './RepairActionCard';
import { useReconciliation } from '../useReconciliation';
import { reconciliationApi } from '../reconciliation-api';
import type { RepairActionResult } from '../../invoice-repair/types';

export function StepNumbering() {
  const { diagnostic, dateFrom, dateTo, updateStepStatus, addRepairLog, isExecuting, setIsExecuting } = useReconciliation();

  const stats = diagnostic?.stats;
  const departments = diagnostic?.departments || [];
  const issues = diagnostic?.issues || [];

  // Track selected prefix per department (for dual-series scenarios)
  const [selectedPrefix, setSelectedPrefix] = useState<Record<number, string>>({});
  // Manual prefix input for when all invoices are TEMP (no prefix detectable)
  const [manualPrefix, setManualPrefix] = useState('');

  useEffect(() => {
    if (!stats) {
      updateStepStatus('numbering', 'idle');
      return;
    }
    const ok = stats.numberingGaps === 0 && stats.wrongMonth === 0 && stats.tempInvoices === 0;
    if (ok) {
      updateStepStatus('numbering', 'done', 'Numeracja ciągła, bez luk');
    } else {
      const parts: string[] = [];
      if (stats.numberingGaps > 0) parts.push(`${stats.numberingGaps} luk`);
      if (stats.wrongMonth > 0) parts.push(`${stats.wrongMonth} z błędnym miesiącem`);
      if (stats.tempInvoices > 0) parts.push(`${stats.tempInvoices} TEMP`);
      updateStepStatus('numbering', 'warning', parts.join(', '));
    }
  }, [stats]);

  if (!diagnostic) {
    return <div className="text-center py-12 text-text-secondary text-sm">Uruchom diagnostykę w kroku 1.</div>;
  }

  const logAction = (action: string, description: string, result: RepairActionResult) => {
    addRepairLog({
      action,
      description,
      changes: (result.invoices || result.results || []).slice(0, 20).map((inv: any) => ({
        invoiceId: inv.id || '',
        field: 'number',
        oldValue: inv.oldNumber || 'TEMP',
        newValue: inv.newNumber || inv.number || '',
        label: inv.orderNumber || '',
      })),
      result: result.failCount && result.failCount > 0 ? 'partial' : 'success',
    });
  };

  const dualSeriesIssue = issues.find(i => i.id === 'dual_series' || i.id === 'dual_numbering');

  return (
    <div className="space-y-4">
      {/* Status */}
      {stats && stats.numberingGaps === 0 && stats.wrongMonth === 0 && stats.tempInvoices === 0 ? (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-success/5 border border-success/20 text-sm text-success">
          <Hash className="w-4 h-4" />
          Numeracja ciągła — brak luk, z błędnym miesiącem ani TEMP faktur
        </div>
      ) : null}

      {/* Frozen invoices info */}
      {(stats as any)?.frozenInvoices > 0 && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-500/5 border border-info/20 text-xs text-accent-info">
          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <div>
            <strong>{(stats as any).frozenInvoices} zamrożonych faktur</strong> z poprzedniego miesiąca (numery zarezerwowane).
            Renumeracja zacznie od numeru {(stats as any).frozenInvoices + 1}.
            Sekwencja: {(stats as any).frozenInvoices} zamrożonych + {departments.reduce((sum, d) => sum + d.vatCount, 0)} bieżących = <strong>{(stats as any).frozenInvoices + departments.reduce((sum, d) => sum + d.vatCount, 0)}</strong> łącznie.
          </div>
        </div>
      )}

      {/* Department breakdown */}
      <div className="space-y-2">
        <h4 className="text-xs font-medium text-text-secondary uppercase tracking-wider">Serie numeracyjne</h4>
        {departments.map(dept => {
          const hasDualSeries = dept.series.length > 1;
          const deptSelectedPrefix = selectedPrefix[dept.departmentId] || dept.series[0]?.prefix || '';
          return (
            <div key={dept.departmentId} className="p-3 rounded-lg bg-surface-raised/50 border border-border-subtle">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-text-secondary">Dział #{dept.departmentId}</span>
                <span className="text-xs text-text-muted">{dept.vatCount} faktur</span>
              </div>
              {dept.series.map((s: any) => (
                <div key={s.prefix} className="flex items-center gap-2 text-xs">
                  <span className="font-mono text-accent-primary">{s.prefix}</span>
                  <span className="text-text-secondary">{s.count} faktur</span>
                </div>
              ))}

              {/* Prefix selector for dual-series departments */}
              {hasDualSeries && (
                <div className="mt-2 pt-2 border-t border-border-subtle">
                  <div className="text-xs text-warning mb-1.5 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Podwójna numeracja — wybierz docelowy prefix:
                  </div>
                  <div className="flex gap-2">
                    {dept.series.map((s: any) => (
                      <button
                        key={s.prefix}
                        onClick={() => setSelectedPrefix(prev => ({ ...prev, [dept.departmentId]: s.prefix }))}
                        className={`px-2 py-1 rounded text-xs font-mono transition-colors ${
                          deptSelectedPrefix === s.prefix
                            ? 'bg-accent-primary/20 text-accent-primary border border-accent-primary/40'
                            : 'bg-surface-hover/50 text-text-secondary border border-border-subtle hover:border-border-strong'
                        }`}
                      >
                        {s.prefix} ({s.count})
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Dual series warning */}
      {dualSeriesIssue && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-500/5 border border-warning/20 text-xs text-warning">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Podwójna numeracja!</strong> Wykryto więcej niż jedną serię w dziale.
            Wybierz docelowy prefix powyżej — renumeracja ujednolici wszystkie faktury do jednego formatu.
          </div>
        </div>
      )}

      {/* TEMP invoices */}
      {stats && stats.tempInvoices > 0 && (() => {
        const dept = departments[0];
        const detectedPrefix = dept?.series?.find((s: any) => s.prefix && !s.prefix.startsWith('_no_match'))?.prefix;
        const allTemp = dept?.series?.every((s: any) => s.prefix.startsWith('_no_match') || s.prefix.startsWith('TEMP'));
        const effectivePrefix = detectedPrefix || manualPrefix || undefined;

        return (
          <>
            {allTemp && (
              <div className="p-3 rounded-lg bg-yellow-500/5 border border-warning/20">
                <div className="text-xs text-warning mb-2 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Wszystkie faktury mają numery TEMP — podaj prefix ręcznie:
                </div>
                <input
                  type="text"
                  value={manualPrefix}
                  onChange={(e) => setManualPrefix(e.target.value)}
                  placeholder="np. MED, FV/, HED/"
                  className="w-full bg-surface-raised border border-warning/20 rounded px-3 py-1.5 text-sm font-mono text-text-primary focus:outline-none focus:border-yellow-500/50"
                />
              </div>
            )}
            <RepairActionCard
              title={`Faktury TEMP${effectivePrefix ? ` (prefix: ${effectivePrefix})` : ''}`}
              description={`${stats.tempInvoices} faktur z numerem TEMP do naprawienia${allTemp && !manualPrefix ? ' — wpisz prefix powyżej' : ''}`}
              count={stats.tempInvoices}
              severity="error"
              disabled={isExecuting || (allTemp && !manualPrefix)}
              onPreview={() => reconciliationApi.fixTemp(dept?.departmentId || 0, true, dateFrom, dateTo, effectivePrefix)}
              onExecute={async () => {
                setIsExecuting(true);
                try {
                  const res = await reconciliationApi.fixTemp(dept?.departmentId || 0, false, dateFrom, dateTo, effectivePrefix);
                  logAction('fix-temp', `Naprawiono ${res.count} faktur TEMP`, res);
                  return res;
                } finally { setIsExecuting(false); }
              }}
            />
          </>
        );
      })()}

      {/* Numbering gaps — renumber per department */}
      {stats && (stats.numberingGaps > 0 || stats.wrongMonth > 0) && departments.map(dept => {
        const prefix = selectedPrefix[dept.departmentId] || dept.series[0]?.prefix;
        return (
          <RepairActionCard
            key={dept.departmentId}
            title={`Renumeracja — Dział #${dept.departmentId}${prefix ? ` (prefix: ${prefix})` : ''}`}
            description={`${stats.numberingGaps} luk w numeracji, ${stats.wrongMonth} z błędnym miesiącem${dept.series.length > 1 ? ' — ujednolicenie do jednej serii' : ''}`}
            count={stats.numberingGaps + stats.wrongMonth}
            severity="warning"
            destructive
            disabled={isExecuting}
            onPreview={() => reconciliationApi.renumber(dept.departmentId, true, dateFrom, dateTo, prefix)}
            onExecute={async () => {
              setIsExecuting(true);
              try {
                const res = await reconciliationApi.renumber(dept.departmentId, false, dateFrom, dateTo, prefix);
                logAction('renumber', `Renumeracja działu #${dept.departmentId}, ${res.count} faktur`, res);
                return res;
              } finally { setIsExecuting(false); }
            }}
          />
        );
      })}
    </div>
  );
}
